import React from 'react'
import {
  ACTIVATION_FAILED_ERRORS,
  DEFAULT_ERROR_CODE,
  DHP_ERROR_CODE,
  DHP_NON_200_ERROR_CODE,
  PORTAL_BRAND,
  SETUP_APP_BRANDING,
  SUPPORT_URL_GENERIC,
  SUPPORT_URL_PRINTER_SETUP,
  SUPPORT_URL_UNABLE_TO_REGISTER,
  TENANT_INTERFACE_ERROR,
  MAX_PAYLOAD_FIELD_LENGTH,
  RETRYABLE_ERRORS_RULES
} from 'src/config/constants'
import resources, { languageFallback, localeFallback } from 'src/assets/locale'
import DOMPurify from 'dompurify'

const packageJson = require('../../package.json')

/**
 * Wraps a dynamic import with retry logic.
 * It will retry up to `retries` times with an exponential back-off delay
 * before finally rejecting.
 */
export const lazyWithRetry = (importFn, retries = 10, delay = 1000) => {
  return React.lazy(async () => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await importFn()
      } catch (error) {
        if (attempt === retries) {
          Logger.error(
            `Failed to load component after ${retries} attempts: ${error}`
          )
          throw error
        }
        await new Promise((resolve) =>
          setTimeout(resolve, delay * Math.pow(2, attempt))
        )
      }
    }
  })
}

export const getCurrentServiceId = () => {
  return window?.Shell?.v1?.store?.state?.onboarding?.sessionContext
    ?.currentServiceId
}

export const logWithPrefix = (severity, log) => {
  console[severity](
    `${getCurrentServiceId()} - ${packageJson.version} - `,
    typeof log === 'string' || log instanceof String ? log : JSON.stringify(log)
  )
}

export const Logger = {
  debug: (log) => logWithPrefix('debug', log),
  log: (log) => logWithPrefix('log', log),
  info: (log) => logWithPrefix('info', log),
  warn: (log) => logWithPrefix('warn', log),
  error: (log) => logWithPrefix('error', log)
}

export const generateError = (error) => {
  const errorCode =
    DHP_ERROR_CODE[error.errorType] ||
    (error.statusCode ? DHP_NON_200_ERROR_CODE : DEFAULT_ERROR_CODE)

  return {
    errorCode: `${getCurrentServiceId()}.${errorCode}`,
    errorDescription: `${error.errorType || error?.statusCode} - ${
      error.reason ? error.reason : 'Error calling DHP plugin (no reason)'
    }`,
    errorCodeLabel: error.statusCode
      ? `${errorCode}_${error.statusCode}`
      : errorCode,
    payload: error.payload,
    resourceUri: error.resourceUri
  }
}

export const isDHPError = (response) => {
  return !!(
    response?.errorType || range(200, 299).indexOf(response?.statusCode) === -1
  )
}

export const range = (start, end) => {
  return Array(end - start + 1)
    .fill(0)
    .map((_, idx) => start + idx)
}

export const generateApiError = (error, resourceUri = null) => {
  const [prefix, status, code] = error.split('_')
  return {
    errorCode: `${getCurrentServiceId()}.${prefix}.${status}.${code}`,
    errorDescription: `${prefix} - ${status} - ${code}`,
    errorCodeLabel: error,
    statusCode: parseInt(status),
    resourceUri: resourceUri
  }
}

export function getHighestResolutionImage(imagesArray) {
  if (!imagesArray) return null

  let highestResolutionImage = null
  let highestResolution = 0

  for (let image of imagesArray) {
    const resolutionComponent = image.split('/').pop()
    const size = resolutionComponent.split('.')[0].split('x')
    const resolution = parseInt(size[0]) * parseInt(size[1])
    if (resolution > highestResolution) {
      highestResolution = resolution
      highestResolutionImage = image
    }
  }

  return highestResolutionImage
}

export const findLocale = (language, country) => {
  const locale = `${language}_${country}`
  return resources[locale] ? locale : findLocaleFallback(language, country)
}

export const findLocaleFallback = (language, country) => {
  const locale = `${language}_${country}`
  if (localeFallback[locale]) {
    return localeFallback[locale]
  }
  return languageFallback[language] ? languageFallback[language] : 'en_US'
}

export const generateErrorCode = (error, fallback) => {
  let errorCode
  try {
    errorCode = error.response.data.errors[0].code
  } catch {
    errorCode = fallback
  }
  return errorCode
}

export const fetchTenantId = async (tenantHandler, accountId) => {
  try {
    let tenantId = tenantHandler.getTenantId('tenant')
    if (!tenantId || tenantId !== accountId) {
      Logger.log('Performing token exchange to set tenant ID')
      await tenantHandler.setTenant({
        tenantId: accountId,
        authContext: 'tenant',
        preventReload: true
      })
      tenantId = tenantHandler.getTenantId('tenant')
    }
    if (!tenantId) {
      throw new Error('Invalid tenantId value')
    }
    return tenantId
  } catch (error) {
    Logger.warn('Unable to perform token exchange and set tenant ID', error)
    throw generateApiError(TENANT_INTERFACE_ERROR)
  }
}

/**
 * Recursively walks any value (string, array, plain object) and applies
 * a string transformation to every string it encounters.
 *
 * @param {*} value - The value to traverse.
 * @param {function(string): string} transform - Function applied to each string.
 * @returns {*} The value with all strings transformed.
 */
export const mapStrings = (value, transform) => {
  if (typeof value === 'string') {
    return transform(value)
  } else if (Array.isArray(value)) {
    value.forEach((item, index) => {
      value[index] = mapStrings(item, transform)
    })
    return value
  } else if (value?.constructor === {}.constructor) {
    Object.keys(value).forEach((key) => {
      value[key] = mapStrings(value[key], transform)
    })
    return value
  }
  return value
}

/**
 * Replaces "HP Smart" with "HP" in any string, array, or plain object,
 * applied recursively. Only runs when the HPX UI brand is active.
 *
 * @param {*} value - The value to process.
 * @param {boolean} isHpxBrand - Whether the HPX brand is currently active.
 * @returns {*} The value with app branding strings replaced.
 */
export const replaceAppBranding = (value, isHpxBrand) => {
  if (!isHpxBrand) return value
  return mapStrings(value, (str) =>
    str.replace(SETUP_APP_BRANDING.HP_SMART, SETUP_APP_BRANDING.HPX)
  )
}

/**
 * Replaces all occurrences of "HP Smart Admin" with "HP Control Hub" in any
 * string, array, or plain object, applied recursively.
 *
 * See: https://hp-jira.external.hp.com/browse/SNO-3110
 *
 * @param {*} value - The value to process.
 * @returns {*} The value with brand strings replaced.
 */
export const replacePortalBranding = (value) =>
  mapStrings(value, (str) =>
    str.replaceAll(PORTAL_BRAND.HP_SMART_ADMIN, PORTAL_BRAND.HP_CONTROL_HUB)
  )

/**
 * Receive a JSON object and sanitize every string found in the object, including all children.
 * This only allows <b>target</b> properties to keep the behavior of anchor links.
 *
 * @param jsonData {{}} JSON that needs to be sanitized
 * @returns {{}} Sanitized JSON object
 */
export const sanitize = (jsonData) => {
  if (!jsonData) {
    return jsonData
  }
  return mapStrings(jsonData, (str) =>
    DOMPurify.sanitize(str, { ADD_ATTR: ['target'] })
  )
}

/**
 * Get the support URI based on the error code, the SMB experience flag and
 * whether the device is a gen2 device.
 *
 * @param errorCode string Error code
 * @param smbExperience boolean SMB experience flag
 * @param isGen2 boolean Gen2 device flag
 * @returns string Support URI
 */
export const getSupportUri = (
  errorCode = null,
  smbExperience = false,
  isGen2 = false
) => {
  if (smbExperience) {
    return SUPPORT_URL_PRINTER_SETUP
  }
  if (isGen2 || errorCode?.includes(ACTIVATION_FAILED_ERRORS.EB000U0422)) {
    return SUPPORT_URL_GENERIC
  }
  return SUPPORT_URL_UNABLE_TO_REGISTER
}

/**
 * Truncates all string values in the given data to MAX_PAYLOAD_FIELD_LENGTH characters:
 * - Returns null if the data is falsy/empty
 * - For string data: truncates to MAX_PAYLOAD_FIELD_LENGTH characters
 * - For object data: returns a shallow copy with all string values truncated to MAX_PAYLOAD_FIELD_LENGTH characters
 *
 * @param {string|Object} data - The data to truncate
 * @returns {string|Object|null} Truncated data, or null if empty
 */
export const truncateData = (data) => {
  if (!data) return null
  return mapStrings(typeof data === 'string' ? data : { ...data }, (str) =>
    str.substring(0, MAX_PAYLOAD_FIELD_LENGTH)
  )
}

/**
 * Checks if the given error code is retryable based on the RETRYABLE_ERRORS_RULES constant.
 *
 * The function passes through all the rules defined in RETRYABLE_ERRORS_RULES:
 * - Returns the retryable config for the last regex rule as that matches the error code.
 * - Returns the first matching code rule as that matches the error code.
 * - If no rules match, it returns false.
 *
 * @param {string} errorCode - Error code label
 * @returns {boolean} True if the error is retryable, false otherwise
 */
export const isRetryableError = (errorCode) => {
  let isRetryable = false
  if (!errorCode) return isRetryable

  for (const rule of RETRYABLE_ERRORS_RULES) {
    if (rule.code === errorCode) return rule.retryable

    if (rule.regex?.test?.(errorCode)) isRetryable = rule.retryable
  }

  return isRetryable
}
